/**
 * Tier access requests.
 *
 * Applications for the application-gated K&C tiers (K2K, Inner Circle) and inquiries for the
 * Enterprise Alfie plan, submitted from the /checkout request modal. The applicant inserts their own row (RLS:
 * user_id = auth.uid()); admins read every row in the dashboard (Subscriptions →
 * Access requests). Mirrors the `tier_access_requests` table in @hermes/db.
 */
import { supabase } from '@/lib/supabase';

export type AccessRequestTier = 'K2K' | 'INNER_CIRCLE' | 'ENTERPRISE';
export type AccessRequestStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn';

/**
 * The application-gated K&C tiers. Both are payable at /signup (pre-account)
 * and /checkout (existing account) once approved. K2K's approval rides
 * `allowed_signups` (sales tag); Inner Circle's rides an approved
 * `tier_access_requests` row (admin review — WS2).
 */
export const APPLICATION_TIER_CODES: readonly AccessRequestTier[] = ['K2K', 'INNER_CIRCLE'];

/** Whether a tier code is application-gated (K2K / Inner Circle). */
export function isApplicationTier(code: string): code is AccessRequestTier {
  return (APPLICATION_TIER_CODES as readonly string[]).includes(code);
}

/**
 * Plans reached by INQUIRY only (Hermes migration 673): the Enterprise Alfie plan. Alfie and the
 * form collect the prospect's details and sales follow up. Kept apart from
 * APPLICATION_TIER_CODES on purpose — there is nothing to buy, so approval grants nothing and
 * the "approved to purchase" gates at /signup and /checkout must never see it.
 */
export const INQUIRY_TIER_CODES: readonly AccessRequestTier[] = ['ENTERPRISE'];

/** Whether a code is an inquiry-only plan (Enterprise). */
export function isInquiryTier(code: string): code is AccessRequestTier {
  return (INQUIRY_TIER_CODES as readonly string[]).includes(code);
}

/** Every code a request can target, for the admin queue: applications, then inquiries. */
export const ACCESS_REQUEST_TIER_CODES: readonly AccessRequestTier[] = [
  ...APPLICATION_TIER_CODES,
  ...INQUIRY_TIER_CODES,
];

/** Human label for a request's tier code. */
export const ACCESS_REQUEST_TIER_LABEL: Record<string, string> = {
  K2K: 'K2K',
  INNER_CIRCLE: 'Inner Circle',
  ENTERPRISE: 'Enterprise',
};

export interface AccessRequestInput {
  userId: string;
  tierCode: AccessRequestTier;
  fullName: string;
  businessName?: string | null;
  email: string;
  message?: string | null;
}

export interface AccessRequestRow {
  id: string;
  userId: string;
  tierCode: string;
  fullName: string;
  businessName: string | null;
  email: string;
  message: string | null;
  status: string;
  createdAt: string;
}

/**
 * Submit the current user's application. RLS enforces user_id = auth.uid().
 *
 * Deduped against an existing OPEN application for the same tier, mirroring
 * the signup-intake path: re-applying is a no-op that reports success, never a
 * second row an admin has to reconcile. Decided rows ('approved'/'rejected')
 * are ignored here, so a rejected applicant can apply again. The database
 * backs this with a partial unique index (migration 446) — a lost race raises
 * 23505, which is likewise reported as a dedupe rather than an error.
 */
export async function submitAccessRequest(
  input: AccessRequestInput,
): Promise<{ deduped: boolean }> {
  const { data: existing, error: readErr } = await supabase
    .from('tier_access_requests')
    .select('id')
    .eq('user_id', input.userId)
    .eq('tier_code', input.tierCode)
    .eq('status', 'pending')
    .limit(1)
    .maybeSingle();
  if (readErr) throw new Error(readErr.message);
  if ((existing as { id: string } | null)?.id) return { deduped: true };

  const business = input.businessName?.trim();
  const message = input.message?.trim();
  const { error } = await supabase.from('tier_access_requests').insert({
    user_id: input.userId,
    tier_code: input.tierCode,
    full_name: input.fullName.trim(),
    business_name: business ? business : null,
    email: input.email.trim(),
    message: message ? message : null,
  });
  if (error) {
    if (error.code === '23505') return { deduped: true };
    throw new Error(error.message);
  }
  return { deduped: false };
}

/**
 * The tier codes this user currently has an OPEN application for — what the
 * checkout ladder renders as "Application pending" instead of inviting a
 * duplicate submission. RLS limits the read to their own rows.
 */
export async function fetchMyPendingAccessTiers(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('tier_access_requests')
    .select('tier_code')
    .eq('user_id', userId)
    .eq('status', 'pending');
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<{ tier_code: string }>).map((r) => r.tier_code);
}

/** The signed-in user's own application per tier, newest first. */
export interface MyAccessRequest {
  tierCode: string;
  status: string;
  /** The reviewer's note on a decided (approved/rejected) row, if any. */
  reviewReason: string | null;
}

/**
 * The user's own applications across ALL statuses (RLS: own rows only) — the
 * richer read behind checkout's per-tier application state: none → "Request
 * access", pending → "Under review", approved → pay, rejected → "Not approved"
 * with the reviewer's reason. Newest-first, so the first row per tier is the
 * live one when a rejected applicant has re-applied.
 */
export async function fetchMyAccessRequests(userId: string): Promise<MyAccessRequest[]> {
  const { data, error } = await supabase
    .from('tier_access_requests')
    .select('tier_code, status, review_reason, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<{
    tier_code: string;
    status: string;
    review_reason: string | null;
  }>).map((r) => ({
    tierCode: r.tier_code,
    status: r.status,
    reviewReason: r.review_reason ?? null,
  }));
}

/** Every submission, newest first. RLS limits this to admins. */
export async function fetchAccessRequests(): Promise<AccessRequestRow[]> {
  const { data, error } = await supabase
    .from('tier_access_requests')
    .select(
      'id, user_id, tier_code, full_name, business_name, email, message, status, created_at',
    )
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);

  return ((data ?? []) as Array<{
    id: string;
    user_id: string;
    tier_code: string;
    full_name: string;
    business_name: string | null;
    email: string;
    message: string | null;
    status: string;
    created_at: string;
  }>).map((r) => ({
    id: r.id,
    userId: r.user_id,
    tierCode: r.tier_code,
    fullName: r.full_name,
    businessName: r.business_name,
    email: r.email,
    message: r.message,
    status: r.status,
    createdAt: r.created_at,
  }));
}
